import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useNotificacionStore } from '@rfacil/shared/hooks';
import { useAuthStore } from '@rfacil/shared/services';
import {
  ChangePassword,
  ContraseniasDTO,
  EditarUsuarioDTO,
  EliminarUsuarioDTO,
  UsuarioCompletoDTO,
  UsuarioCorporativoCompletoDTO,
  UsuarioDTO,
  getUserById,
  getUsersCorporative,
  getUserCorporateById,
  getUserCompanyById,
  getUserOperativeById,
  createUser,
  editUser,
  deleteUser,
  createUserCorporate,
  createUserCompany,
  createUserOperative,
  getUserOperativeInvoice,
} from '../services/usuarioService';
import {
  CrearUsuarioDetalleDTO,
  EditarUsuarioDetalleDTO,
  createUserDet,
  editUserDet,
} from '../services/usuarioDetalle.Service';

/**
 * Query keys para las queries de usuarios
 */
/** Raíz de la query en `DashboardRoleOverview` (totales por rol); invalidar al mutar usuarios. */
export const dashboardOverviewKeys = {
  all: ['dashboard-overview'] as const,
};

export const userKeys = {
  all: ['usuarios'] as const,
  lists: () => [...userKeys.all, 'list'] as const,
  list: (filters?: string) => [...userKeys.lists(), filters] as const,
  details: () => [...userKeys.all, 'detail'] as const,
  detail: (id: number) => [...userKeys.details(), id] as const,
  corporativos: () => [...userKeys.all, 'corporativos'] as const,
  corporativo: (id: number) => [...userKeys.corporativos(), id] as const,
  empresas: (idCorporativo: number) => [...userKeys.all, 'empresas', idCorporativo] as const,
  operativos: (idEmpresa: number) => [...userKeys.all, 'operativos', idEmpresa] as const,
  operativosInvoice: (idEmpresa: number) => [...userKeys.all, 'operativosInvoice', idEmpresa] as const,
};

/**
 * Hook para obtener un usuario por ID
 */
export function useUser(id: number | null | undefined) {
  return useQuery({
    queryKey: userKeys.detail(id ?? 0),
    queryFn: () => {
      if (!id || id <= 0) {
        throw new Error('ID de usuario inválido');
      }
      return getUserById(id);
    },
    enabled: !!id && id > 0,
  });
}

/**
 * Hook para obtener todos los usuarios corporativos
 */
export function useUsersCorporative() {
  return useQuery({
    queryKey: userKeys.corporativos(),
    queryFn: getUsersCorporative,
  });
}

/**
 * Hook para obtener usuarios según el rol del usuario logueado
 * - Super: usuarios corporativos
 * - Corporativo: usuarios de empresa por IdCorporativo
 * - Empresa: usuarios operativos por IdEmpresa
 */
export function useUsersByRole(
  isSuper: boolean,
  isCorporativo: boolean,
  isEmpresa: boolean,
  idCorporativo?: number | null,
  idEmpresa?: number | null
) {
  return useQuery<UsuarioDTO[]>({
    queryKey: ['usuarios', 'byRole', isSuper, isCorporativo, isEmpresa, idCorporativo, idEmpresa],
    queryFn: async () => {
      if (isSuper) {
        const corporateUsers = await getUsersCorporative();
        // Convertir UserCorporativoDTO[] a UsuarioDTO[]
        return corporateUsers.map((user) => ({
          Id: user.Id,
          Usuario: user.Usuario,
          Correo: user.Correo,
          ContraseniaEncriptada: user.ContraseniaEncriptada,
          Estatus: user.Estatus,
          FechaAlta: user.FechaAlta,
          FechaBaja: user.FechaBaja,
          Rol: user.Rol,
          BorradoLogico: user.BorradoLogico,
          NombreCompleto: user.NombreCompleto,
          IdEmpresa: 0,
          IdCorporativo: user.IdCorporativo,
          // Campos opcionales para mantener compatibilidad con la tabla
          NombreEmpresa: null,
          NombreCorporativo: user.NombreCorporativo ?? null,
        }));
      } else if (isCorporativo && idCorporativo) {
        return getUserCompanyById(idCorporativo);
      } else if (isEmpresa && idEmpresa) {
        return getUserOperativeById(idEmpresa);
      }
      return [];
    },
    enabled: (isSuper || (isCorporativo && !!idCorporativo) || (isEmpresa && !!idEmpresa)),
  });
}

/**
 * Hook para obtener usuarios corporativos por ID de corporativo
 */
export function useUsersCorporativeById(id: number | null | undefined) {
  return useQuery({
    queryKey: userKeys.corporativo(id ?? 0),
    queryFn: () => {
      if (!id || id <= 0) {
        throw new Error('ID de corporativo inválido');
      }
      return getUserCorporateById(id);
    },
    enabled: !!id && id > 0,
  });
}

/**
 * Hook para obtener usuarios de empresa por ID de corporativo
 */
export function useUsersCompanyById(idCorporativo: number | null | undefined) {
  return useQuery({
    queryKey: userKeys.empresas(idCorporativo ?? 0),
    queryFn: () => {
      if (!idCorporativo || idCorporativo <= 0) {
        throw new Error('ID de corporativo inválido');
      }
      return getUserCompanyById(idCorporativo);
    },
    enabled: !!idCorporativo && idCorporativo > 0,
  });
}

/**
 * Hook para obtener usuarios operativos por ID de empresa
 */
export function useUsersOperativeById(idEmpresa: number | null | undefined) {
  return useQuery({
    queryKey: userKeys.operativos(idEmpresa ?? 0),
    queryFn: () => {
      if (!idEmpresa || idEmpresa <= 0) {
        throw new Error('ID de empresa inválido');
      }
      return getUserOperativeById(idEmpresa);
    },
    enabled: !!idEmpresa && idEmpresa > 0,
  });
}

/**
 * Hook para obtener usuarios operativos para facturas por ID de empresa
 */
export function useUsersOperativeInvoice(idEmpresa: number | null | undefined) {
  return useQuery({
    queryKey: userKeys.operativosInvoice(idEmpresa ?? 0),
    queryFn: () => {
      if (!idEmpresa || idEmpresa <= 0) {
        throw new Error('ID de empresa inválido');
      }
      return getUserOperativeInvoice(idEmpresa);
    },
    enabled: !!idEmpresa && idEmpresa > 0,
  });
}


/**
 * Hook para crear un usuario
 */
export function useCreateUser() {
  const queryClient = useQueryClient();
  const { showNotification } = useNotificacionStore();

  return useMutation({
    mutationFn: (data: UsuarioCompletoDTO) => createUser(data),
    onSuccess: () => {
      // Invalidar las queries relacionadas para refrescar los datos
      queryClient.invalidateQueries({ queryKey: userKeys.all });
      queryClient.invalidateQueries({ queryKey: dashboardOverviewKeys.all });
      showNotification('Usuario creado exitosamente', 'success');
    },
    onError: (error: Error) => {
      console.error('Error al crear usuario:', error);
      showNotification(error.message || 'Error al crear usuario. Por favor intenta nuevamente.', 'error');
    },
  });
}

/**
 * Hook para crear un usuario corporativo
 */
export function useCreateUserCorporative() {
  const queryClient = useQueryClient();
  const { showNotification } = useNotificacionStore();

  return useMutation({
    mutationFn: (data: UsuarioCorporativoCompletoDTO) => createUserCorporate(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: userKeys.all });
      queryClient.invalidateQueries({ queryKey: dashboardOverviewKeys.all });
      showNotification('Usuario corporativo creado exitosamente', 'success');
    },
    onError: (error: Error) => {
      console.error('Error al crear usuario corporativo:', error);
      showNotification(error.message || 'Error al crear usuario corporativo. Por favor intenta nuevamente.', 'error');
    },
  });
}

/**
 * Hook para crear un usuario de empresa
 */
export function useCreateUserCompany() {
  const queryClient = useQueryClient();
  const { showNotification } = useNotificacionStore();

  return useMutation({
    mutationFn: (data: UsuarioCompletoDTO) => createUserCompany(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: userKeys.all });
      queryClient.invalidateQueries({ queryKey: dashboardOverviewKeys.all });
      showNotification('Usuario de empresa creado exitosamente', 'success');
    },
    onError: (error: Error) => {
      console.error('Error al crear usuario de empresa:', error);
      showNotification(error.message || 'Error al crear usuario de empresa. Por favor intenta nuevamente.', 'error');
    },
  });
}

/**
 * Hook para crear un usuario operativo
 */
export function useCreateUserOperative() {
  const queryClient = useQueryClient();
  const { showNotification } = useNotificacionStore();

  return useMutation({
    mutationFn: (data: UsuarioCompletoDTO) => createUserOperative(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: userKeys.all });
      queryClient.invalidateQueries({ queryKey: dashboardOverviewKeys.all });
      showNotification('Usuario operativo creado exitosamente', 'success');
    },
    onError: (error: Error) => {
      console.error('Error al crear usuario operativo:', error);
      showNotification(error.message || 'Error al crear usuario operativo. Por favor intenta nuevamente.', 'error');
    },
  });
}

/**
 * Hook para editar un usuario
 */
export function useEditUser() {
  const queryClient = useQueryClient();
  const { showNotification } = useNotificacionStore();

  return useMutation({
    mutationFn: (data: EditarUsuarioDTO) => editUser(data),
    onSuccess: (_, variables) => {
      // Invalidar la query específica del usuario y todas las listas
      queryClient.invalidateQueries({ queryKey: userKeys.detail(variables.Id) });
      queryClient.invalidateQueries({ queryKey: userKeys.all });
      queryClient.invalidateQueries({ queryKey: dashboardOverviewKeys.all });
      showNotification('Usuario actualizado exitosamente', 'success');
    },
    onError: (error: Error) => {
      console.error('Error al editar usuario:', error);
      showNotification(error.message || 'Error al editar usuario. Por favor intenta nuevamente.', 'error');
    },
  });
}

/** Variables de `useGuardarDetalleUsuario`: el detalle que ya existe se edita; si el usuario aún no tiene, se crea. */
export type GuardarDetalleUsuario =
  | { accion: 'editar'; detalle: EditarUsuarioDetalleDTO }
  | { accion: 'crear'; detalle: CrearUsuarioDetalleDTO };

/**
 * Hook para guardar el detalle (nombre y apellidos) de un usuario que se está editando, después de `useEditUser`.
 * Esa mutación ya invalida los usuarios, pero la recarga sale antes de que el detalle se guarde y la lista se
 * quedaba con el nombre viejo: por eso se vuelven a invalidar al terminar. Los errores los maneja quien llama.
 */
export function useGuardarDetalleUsuario() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (variables: GuardarDetalleUsuario) =>
      variables.accion === 'editar' ? editUserDet(variables.detalle) : createUserDet(variables.detalle),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: userKeys.all });
    },
  });
}

/**
 * Hook para eliminar un usuario (soft delete)
 */
export function useDeleteUser() {
  const queryClient = useQueryClient();
  const { showNotification } = useNotificacionStore();

  return useMutation({
    mutationFn: (data: EliminarUsuarioDTO) => deleteUser(data),
    onSuccess: (_, variables) => {
      // Invalidar la query específica del usuario y todas las listas
      queryClient.invalidateQueries({ queryKey: userKeys.detail(variables.Id) });
      queryClient.invalidateQueries({ queryKey: userKeys.all });
      queryClient.invalidateQueries({ queryKey: dashboardOverviewKeys.all });
      showNotification('Usuario eliminado exitosamente', 'success');
    },
    onError: (error: Error) => {
      console.error('Error al eliminar usuario:', error);
      showNotification(error.message || 'Error al eliminar usuario. Por favor intenta nuevamente.', 'error');
    },
  });
}

/**
 * Hook para cambiar la contraseña del usuario (SSO)
 */
export function useChangePassword() {
  const { showNotification } = useNotificacionStore();

  return useMutation({
    mutationFn: (data: ContraseniasDTO) => ChangePassword(data),
    onSuccess: () => {
      useAuthStore.getState().clearPendingPasswordChange();
      showNotification('Contraseña actualizada exitosamente', 'success');
    },
    onError: (error) => {
      console.error('Error al cambiar contraseña:', error);
      const message =
        error instanceof Error
          ? error.message
          : 'Error al cambiar la contraseña. Por favor intenta nuevamente.';
      showNotification(message, 'error');
    },
  });
}
